import React from 'react';
import '../HumanResources.css';

function FinanceManager() {
  return (
    <div className="hr-page">
      <h1>FINANCE MANAGER</h1>

      {/* Overview Section */}
      <h2>Overview</h2>
      <p>
        A Finance Manager is responsible for overseeing the financial health of an organization, including managing financial planning, budgeting, forecasting, accounting, and reporting. They provide strategic financial advice to senior management to aid in decision-making and ensure the company's financial stability and growth.
      </p>
      <ul>
        <li>
          <strong>Financial Planning and Analysis:</strong> Developing financial plans, budgets, and forecasts; analyzing financial performance against targets; identifying trends and providing insights; conducting variance analysis.
        </li>
        <li>
          <strong>Budgeting and Forecasting:</strong> Leading the annual budgeting process; monitoring budget performance; preparing rolling forecasts; collaborating with department heads on budget management.
        </li>
        <li>
          <strong>Financial Reporting:</strong> Preparing accurate and timely financial statements (income statements, balance sheets, cash flow statements); ensuring compliance with accounting standards and regulations; presenting financial reports to management and stakeholders.
        </li>
        <li>
          <strong>Accounting Operations:</strong> Overseeing day-to-day accounting functions, including accounts payable, accounts receivable, general ledger, and payroll; ensuring accuracy and efficiency of accounting processes.
        </li>
        <li>
          <strong>Cash Flow Management:</strong> Monitoring and managing the company's cash flow; preparing cash flow forecasts; ensuring sufficient liquidity to meet operational needs.
        </li>
        <li>
          <strong>Risk Management:</strong> Identifying and assessing financial risks; developing and implementing strategies to mitigate risks; ensuring compliance with internal controls.
        </li>
        <li>
          <strong>Compliance and Regulations:</strong> Staying up-to-date with relevant financial laws and regulations; ensuring the company's financial practices comply with requirements.
        </li>
        <li>
          <strong>Liaison with External Parties:</strong> Interacting with auditors, tax advisors, bankers, and other external stakeholders.
        </li>
        <li>
          <strong>Team Management:</strong> Supervising and mentoring finance team members; delegating tasks and ensuring team performance.
        </li>
        <li>
          <strong>Strategic Financial Advice:</strong> Providing financial insights and recommendations to senior management on strategic initiatives and business opportunities.
        </li>
        <li>
          <strong>Financial Systems and Processes:</strong> Evaluating and improving financial systems, processes, and internal controls.
        </li>
      </ul>

      {/* Qualifications Section */}
      <h2>Qualifications</h2>
      <ul>
        <li>Bachelor's degree in Finance, Accounting, Economics, or a related field; Master's degree or professional certification (e.g., CPA, ACCA, CIMA) preferred.</li>
        <li>Proven experience in finance and accounting, with several years in progressively responsible roles.</li>
        <li>Strong understanding of accounting principles (IFRS, GAAP) and financial regulations.</li>
        <li>Excellent analytical, problem-solving, and decision-making skills.</li>
        <li>Proficiency in financial modeling and data analysis.</li>
        <li>Advanced IT skills, including Excel and accounting software/ERP systems (e.g., Xero).</li>
        <li>Excellent communication and presentation skills for non-financial stakeholders.</li>
        <li>Strong leadership and interpersonal skills when managing a team.</li>
        <li>Ethical and professional conduct.</li>
      </ul>

      {/* Proposed Job Levels Section */}
      <h2>Proposed Job Levels for the Finance Manager Role</h2>
      <p>
        This structure provides a progression within the finance function at Nesim, from junior support roles to strategic leadership positions.
      </p>

      {/* Level 1 */}
      <h3>Level 1: Junior Finance Manager / Assistant Finance Manager</h3>
      <ul>
        <li><strong>Focus:</strong> Supporting finance functions like report preparation, assisting budgeting, processing transactions, and record maintenance.</li>
        <li>
          <strong>Key Responsibilities:</strong>
          <ul>
            <li>Preparing basic financial reports and maintaining records.</li>
            <li>Assisting with budgeting and transaction processing.</li>
            <li>Performing variance analysis and data entry.</li>
            <li>Supporting accounts payable/receivable and payroll tasks.</li>
          </ul>
        </li>
        <li><strong>Experience:</strong> 2–4 years in finance or accounting roles.</li>
      </ul>

      {/* Level 2 */}
      <h3>Level 2: Finance Manager</h3>
      <ul>
        <li><strong>Focus:</strong> Managing budgeting, forecasting, reporting, and analysis at a departmental or business-unit level.</li>
        <li>
          <strong>Key Responsibilities:</strong>
          <ul>
            <li>Developing and monitoring budgets and forecasts.</li>
            <li>Preparing financial statements and conducting performance analysis.</li>
            <li>Coordinating with department heads for budget management.</li>
            <li>Supervising a small team of analysts or accountants.</li>
          </ul>
        </li>
        <li><strong>Experience:</strong> 5+ years of progressive finance experience.</li>
      </ul>

      {/* Level 3 */}
      <h3>Level 3: Senior Finance Manager</h3>
      <ul>
        <li><strong>Focus:</strong> Strategic financial planning, high-level advice, and oversight of key financial processes.</li>
        <li>
          <strong>Key Responsibilities:</strong>
          <ul>
            <li>Developing long-term financial strategies and leading complex projects.</li>
            <li>Providing high-level financial insights to senior management.</li>
            <li>Overseeing multiple departments or business units.</li>
            <li>Leading and mentoring larger finance teams.</li>
          </ul>
        </li>
        <li><strong>Experience:</strong> 8+ years of significant finance experience.</li>
      </ul>

      {/* Level 4 */}
      <h3>Level 4: Head of Finance / Finance Director</h3>
      <ul>
        <li><strong>Focus:</strong> Leading the entire finance function and aligning financial strategy with business objectives.</li>
        <li>
          <strong>Key Responsibilities:</strong>
          <ul>
            <li>Setting finance department direction and policy.</li>
            <li>Managing financial risks and ensuring board-level reporting.</li>
            <li>Overseeing strategic decision-making and company-wide financial operations.</li>
          </ul>
        </li>
        <li><strong>Experience:</strong> 10+ years in senior finance roles with demonstrated leadership.
        </li>
      </ul>
    </div>
  );
}

export default FinanceManager;
